/**
 * DOZY OPERATIONS — o funil de recrutamento.
 *
 * Não é o Dashboard do admin com outros números: aquele mede o negócio
 * (receita, pedidos, clientes), este mede a construção da oferta. São
 * perguntas diferentes e não devem partilhar ecrã.
 *
 * Os alertas vêm primeiro porque são o que exige acção hoje; o funil vem a
 * seguir porque é o que se reporta ao fim da semana.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Loader2, MapPin, ArrowRight, TrendingUp } from 'lucide-react';
import api from '../../utils/api';

const CORES = {
  pending:  'text-slate-300',
  verified: 'text-blue-400',
  approved: 'text-purple-400',
  trained:  'text-cyan-400',
  active:   'text-green-400',
  rejected: 'text-red-400',
  returned: 'text-amber-400',
};

const OperationsHome = () => {
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [alertas, setAlertas] = useState([]);

  useEffect(() => {
    api.get('/operations/metrics').then((r) => setM(r.data)).catch(() => setM({ geral: {}, zonas: [] }));
    api.get('/operations/alerts').then((r) => setAlertas(r.data || [])).catch(() => setAlertas([]));
  }, []);

  const irPara = (fase, zona) => {
    const p = new URLSearchParams();
    if (fase) p.set('fase', fase);
    if (zona) p.set('zona', zona);
    navigate(`/admin/pipeline?${p.toString()}`);
  };

  if (!m) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>;
  }

  const g = m.geral || {};
  const funil = [
    ['pending', 'Candidatos'],
    ['verified', 'Documentação validada'],
    ['approved', 'Entrevista aprovada'],
    ['trained', 'Formação concluída'],
    ['active', 'Activos'],
  ];

  // Quantos chegaram ao fim, de todos os que entraram. É o número que diz se
  // a operação está a funcionar — mais do que o total de candidatos.
  const totalEntrados = (g.total || 0) - (g.rejected || 0);
  const conversao = totalEntrados > 0 ? Math.round(((g.active || 0) / totalEntrados) * 100) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Operações</h1>
        <p className="text-sm text-slate-400 mt-1">Recrutamento e activação de profissionais.</p>
      </div>

      {alertas.length > 0 && (
        <section>
          <h2 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">A precisar de si</h2>
          <div className="space-y-2">
            {alertas.map((a) => (
              <button
                key={a.chave}
                onClick={() => irPara(a.fase)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left
                  ${a.grave ? 'bg-red-500/10 border-red-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}
              >
                <AlertTriangle className={`w-5 h-5 shrink-0 ${a.grave ? 'text-red-400' : 'text-amber-400'}`} />
                <span className="flex-1 text-sm">
                  <strong className={a.grave ? 'text-red-300' : 'text-amber-300'}>{a.n}</strong>
                  <span className="text-slate-300"> {a.texto}</span>
                </span>
                <ArrowRight className="w-4 h-4 text-slate-500 shrink-0" />
              </button>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="flex items-baseline gap-3 mb-2">
          <h2 className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex-1">Funil</h2>
          {totalEntrados > 0 && (
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5 text-green-400" />
              {conversao}% chegam a activos
            </span>
          )}
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 divide-y divide-slate-800">
          {funil.map(([chave, rotulo]) => (
            <button
              key={chave}
              onClick={() => irPara(chave)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-800/50"
            >
              <span className={`text-2xl font-bold tabular-nums w-14 ${CORES[chave]}`}>{g[chave] || 0}</span>
              <span className="flex-1 text-sm text-slate-300">{rotulo}</span>
              <ArrowRight className="w-4 h-4 text-slate-600" />
            </button>
          ))}
          {(g.returned > 0 || g.rejected > 0) && (
            <div className="flex gap-4 px-4 py-3 text-sm">
              {g.returned > 0 && (
                <button onClick={() => irPara('returned')} className="text-amber-400 hover:underline">
                  {g.returned} devolvidos
                </button>
              )}
              {g.rejected > 0 && (
                <button onClick={() => irPara('rejected')} className="text-red-400 hover:underline">
                  {g.rejected} rejeitados
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      <section>
        <h2 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Por zona</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(m.zonas || []).map((z) => (
            <div key={z.zone_id} className="p-4 rounded-xl border border-slate-800 bg-slate-900/50">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4 text-blue-400" />
                <span className="font-semibold text-white">{z.nome}</span>
                <span className="ml-auto text-xs text-slate-600 font-mono">{z.codigo}</span>
              </div>
              <dl className="grid grid-cols-3 gap-2 text-center">
                {[['Candidatos', z.pending, 'text-slate-300'],
                  ['Verificados', z.verified, 'text-blue-400'],
                  ['Activos', z.active, 'text-green-400']].map(([r, v, c]) => (
                  <div key={r}>
                    <dd className={`text-xl font-bold tabular-nums ${c}`}>{v || 0}</dd>
                    <dt className="text-[11px] text-slate-500">{r}</dt>
                  </div>
                ))}
              </dl>
              <button
                onClick={() => irPara(null, z.zone_id)}
                className="w-full mt-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs font-medium"
              >
                Ver os {z.trabalhadores || 0} desta zona
              </button>
            </div>
          ))}
          {(m.zonas || []).length === 0 && (
            <p className="text-sm text-slate-500">Ainda não há zonas com profissionais.</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default OperationsHome;
